/**
 * Message/Activity Log — PTT history, text, images, broadcasts + audio recordings tab.
 * Talk2 visual rebrand: light glass surfaces, multicolor accents, iMessage bubbles.
 */
import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useStore from '../store/useStore';
import axios from 'axios';
import { Mic, Radio, Megaphone, MessageSquare, FileAudio, Play, Pause, X, ChevronDown, MoreHorizontal, Edit2, Trash2, Search } from 'lucide-react';

const EMPTY_ARRAY = [];
const LANG_FLAGS = { es: '🇲🇽', en: '🇺🇸', fr: '🇫🇷', de: '🇩🇪', pt: '🇧🇷' };

const formatTime = (ts) =>
  new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const formatDuration = (s) => {
  if (!s) return '0s';
  return s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.16)',
        border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
        color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><X size={18} /></button>
      <img src={src} alt="fullscreen" onClick={e => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 14, boxShadow: '0 0 60px rgba(0,0,0,0.7)' }} />
    </div>
  );
}

// ── Audio Recording Player ────────────────────────────────────────────────────
function RecordingItem({ rec }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const [showTranscription, setShowTranscription] = useState(false);

  const toggle = useCallback(async () => {
    if (!rec.audioUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(rec.audioUrl);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (e) {
        console.warn('[RecordingItem] play blocked:', e);
      }
    }
  }, [playing, rec.audioUrl]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: '10px 12px', borderRadius: 14,
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.05)',
      boxShadow: '0 1px 2px rgba(0,0,0,.04)',
      marginBottom: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%',
          background: `linear-gradient(135deg, ${rec.avatarColor || '#007AFF'}, #AF52DE)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,.06)',
        }}>
          {rec.username?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1D1D1F', letterSpacing: '-0.1px' }}>{rec.username}</div>
          <div style={{ fontSize: 11, color: '#86868B', fontFamily: 'var(--rt-font-mono)' }}>
            {formatTime(rec.createdAt)} · {formatDuration(rec.duration)}
          </div>
        </div>
        {rec.audioUrl && (
          <button onClick={toggle} style={{
            flexShrink: 0, padding: '6px 12px', borderRadius: 999,
            background: playing ? 'linear-gradient(135deg,#007AFF,#AF52DE)' : '#E6F1FF',
            border: 'none',
            color: playing ? '#fff' : '#007AFF',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
            boxShadow: playing ? '0 4px 10px rgba(0,122,255,.30)' : 'none',
          }}>
            {playing ? <Pause size={12} /> : <Play size={12} />}
            {playing ? 'Pausar' : 'Escuchar'}
          </button>
        )}
      </div>
      {rec.transcription && (
        <div style={{ marginTop: 4 }}>
          <button onClick={() => setShowTranscription(v => !v)}
            style={{ fontSize: 10, color: '#86868B', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, padding: 0, fontWeight: 600 }}>
            <ChevronDown size={11} style={{ transform: showTranscription ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }} />
            Transcripción
          </button>
          {showTranscription && (
            <p style={{ fontSize: 12, color: '#48484A', fontStyle: 'italic', marginTop: 4, paddingLeft: 14, lineHeight: 1.4 }}>
              {rec.transcription}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Recordings Tab ────────────────────────────────────────────────────────────
function RecordingsTab({ channelId }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  const load = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    const off = reset ? 0 : offset;
    try {
      const res = await axios.get(`/api/transmissions/${channelId}?limit=${LIMIT}&offset=${off}`);
      const data = res.data;
      setRecordings(prev => reset ? data : [...prev, ...data]);
      setOffset(off + data.length);
      setHasMore(data.length === LIMIT);
    } catch (e) {
      console.error('[RecordingsTab]', e);
    } finally {
      setLoading(false);
    }
  }, [channelId, offset, loading]);

  useEffect(() => { load(true); }, [channelId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && recordings.length === 0) {
    return (
      <div style={{ flex: 1, padding: 12 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="fx-skel" style={{ height: 52, marginBottom: 8, opacity: 1 - i * 0.18 }} />
        ))}
      </div>
    );
  }

  if (recordings.length === 0 && !loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <FileAudio size={28} style={{ color: '#AEAEB2', marginBottom: 8 }} />
        <p style={{ fontSize: 12, color: '#86868B' }}>Sin grabaciones en este canal</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }} className="scrollbar-thin">
      {recordings.map(r => <RecordingItem key={r.id} rec={r} />)}
      {hasMore && (
        <button onClick={() => load(false)} disabled={loading}
          style={{ width: '100%', padding: '8px', marginTop: 4, background: '#F5F5F7', border: 'none', borderRadius: 10, color: '#007AFF', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {loading ? 'Cargando…' : 'Cargar más'}
        </button>
      )}
    </div>
  );
}

// ── Markdown / mention render ─────────────────────────────────────────────────
// Renders message content with markdown (bold/italic/code/links/list) and
// highlights @mentions. Falls back to placeholder when message is soft-deleted.
function renderMentions(text) {
  // Split text on @username tokens and wrap matches in styled span.
  const parts = String(text).split(/(\@[\w-]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return (
        <span key={i} style={{
          color: '#007AFF', fontWeight: 600,
          background: 'rgba(0,122,255,0.08)',
          padding: '0 4px', borderRadius: 6,
        }}>{p}</span>
      );
    }
    return p;
  });
}

const MDComponents = {
  p: ({ children }) => <p style={{ margin: 0, lineHeight: 1.4 }}>{children}</p>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{children}</a>
  ),
  code: ({ inline, children }) => inline
    ? <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4, fontSize: '0.95em', fontFamily: 'var(--rt-font-mono)' }}>{children}</code>
    : <pre style={{ background: 'rgba(0,0,0,0.06)', padding: 8, borderRadius: 8, overflowX: 'auto', fontSize: '0.95em', fontFamily: 'var(--rt-font-mono)', margin: '4px 0' }}><code>{children}</code></pre>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
};

function MessageContent({ msg, isSelf }) {
  if (msg.deletedAt) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, fontStyle: 'italic', opacity: 0.7 }}>
        mensaje eliminado
      </p>
    );
  }
  const content = msg.content || '';
  // Pre-process @mentions so they render even if user wrote them inside markdown
  return (
    <div style={{ fontSize: 13.5, wordBreak: 'break-word', lineHeight: 1.4 }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MDComponents}>
        {content}
      </ReactMarkdown>
      {/* If markdown stripped @mention emphasis, we still want highlighted style.
          Cheap solution: when content has no markdown syntax, fall back to span render. */}
    </div>
  );
}

// ── Actions menu (edit / delete) ──────────────────────────────────────────────
function MessageActions({ msg, channelId, isSelf, currentUser }) {
  const updateMessage = useStore(s => s.updateMessage);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.content || '');
  const [busy, setBusy] = useState(false);
  const isAdmin = currentUser?.role === 'admin';
  if (!isSelf && !isAdmin) return null;
  if (msg.deletedAt) return null;

  const startEdit = () => { setDraft(msg.content || ''); setEditing(true); setOpen(false); };
  const cancel = () => { setEditing(false); setDraft(msg.content || ''); };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === msg.content) { setEditing(false); return; }
    setBusy(true);
    try {
      const res = await axios.patch(`/api/channels/${channelId}/messages/${msg.id}`, { content: trimmed });
      updateMessage(channelId, msg.id, { content: res.data.content, editedAt: res.data.editedAt, mentions: res.data.mentions });
      setEditing(false);
    } catch (e) {
      console.error('[edit message]', e?.response?.data || e);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    setBusy(true);
    try {
      await axios.delete(`/api/channels/${channelId}/messages/${msg.id}`);
      updateMessage(channelId, msg.id, { deletedAt: new Date().toISOString(), content: null });
      setOpen(false);
    } catch (e) {
      console.error('[delete message]', e?.response?.data || e);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={2}
          autoFocus
          aria-label="Editar mensaje"
          style={{
            width: '100%', resize: 'vertical', minHeight: 40,
            padding: '6px 8px', fontSize: 13, borderRadius: 8,
            border: '1px solid rgba(0,0,0,0.12)',
            background: '#fff', color: '#1D1D1F', fontFamily: 'inherit',
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') cancel();
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save();
          }}
        />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button onClick={cancel} disabled={busy}
            type="button" aria-label="Cancelar edición"
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#F5F5F7', border: 'none', cursor: 'pointer', color: '#48484A', fontWeight: 600 }}>
            Cancelar
          </button>
          <button onClick={save} disabled={busy || !draft.trim()}
            type="button" aria-label="Guardar edición"
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#007AFF', border: 'none', cursor: busy ? 'wait' : 'pointer', color: '#fff', fontWeight: 700 }}>
            {busy ? '…' : 'Guardar'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label="Acciones de mensaje"
        title="Más"
        onClick={() => setOpen(v => !v)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: isSelf ? 'rgba(255,255,255,0.7)' : '#86868B',
          padding: 2, borderRadius: 4, display: 'inline-flex',
        }}
      >
        <MoreHorizontal size={14} aria-hidden="true" />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, zIndex: 50, marginTop: 2,
            minWidth: 140, background: '#fff', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.06)',
            padding: 4,
          }}>
            {isSelf && (
              <button onClick={startEdit} type="button"
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#1D1D1F' }}>
                <Edit2 size={12} aria-hidden="true" /> Editar
              </button>
            )}
            <button onClick={del} type="button" disabled={busy}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#FF3B30' }}>
              <Trash2 size={12} aria-hidden="true" /> Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Search bar within channel ─────────────────────────────────────────────────
function ChannelSearch({ channelId, onJump }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/channels/${channelId}/messages/search`, { params: { q } });
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setResults([]);
      } finally { setLoading(false); }
    }, 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, channelId]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderBottom: '1px solid rgba(0,0,0,0.05)', background: 'rgba(255,255,255,0.5)' }}>
        <Search size={13} aria-hidden="true" style={{ color: '#86868B', flexShrink: 0 }} />
        <input
          type="search"
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar en este canal"
          aria-label="Buscar mensajes en este canal"
          style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#1D1D1F' }}
        />
        {q && (
          <button onClick={() => { setQ(''); setResults([]); setOpen(false); }} type="button" aria-label="Limpiar búsqueda"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#86868B' }}>
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && q.length >= 2 && (
        <div className="scrollbar-thin" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
          maxHeight: 260, overflowY: 'auto',
          background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        }}>
          {loading && <div style={{ padding: 10, fontSize: 11, color: '#86868B' }}>Buscando…</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: 10, fontSize: 11, color: '#86868B' }}>Sin resultados</div>
          )}
          {results.map(r => (
            <button key={r.id} onClick={() => { onJump?.(r.id); setOpen(false); }} type="button"
              style={{ width: '100%', textAlign: 'left', padding: '6px 10px', background: 'transparent', border: 'none', borderTop: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer' }}>
              <div style={{ fontSize: 11, color: '#86868B', fontWeight: 600 }}>{r.User?.username || ''} · {formatTime(r.createdAt)}</div>
              <div style={{ fontSize: 12, color: '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.content}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main MessageLog ───────────────────────────────────────────────────────────
export default function MessageLog({ channelId }) {
  const messages = useStore(s => s.messages[channelId] ?? EMPTY_ARRAY);

  // SOS entrante → flash de pantalla (solo para SOS nuevos, no al cargar historial)
  const [sosFlash, setSosFlash] = useState(false);
  const seenSosRef = useRef(new Set());
  const sosReadyRef = useRef(false);
  useEffect(() => {
    const fresh = messages.filter(m => m.type === 'sos' && m.id && !seenSosRef.current.has(m.id));
    fresh.forEach(m => seenSosRef.current.add(m.id));
    if (!sosReadyRef.current) { sosReadyRef.current = true; return; }
    if (fresh.length) {
      setSosFlash(true);
      const t = setTimeout(() => setSosFlash(false), 1700);
      return () => clearTimeout(t);
    }
  }, [messages]);
  const currentUser = useStore(s => s.user);
  const bottomRef = useRef(null);
  const [tab, setTab] = useState('chat'); // 'chat' | 'recordings'
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (tab === 'chat') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, tab]);

  const tabBtn = (id, label, Icon) => (
    <button onClick={() => setTab(id)}
      style={{
        flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: 700,
        background: 'transparent',
        border: 'none', borderBottom: tab === id ? '2px solid #007AFF' : '2px solid transparent',
        color: tab === id ? '#007AFF' : '#86868B', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        letterSpacing: '0.3px',
      }}>
      <Icon size={11} />{label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tabs */}
      {sosFlash && <div className="fx-sos-flash" aria-hidden="true" />}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0, background: 'rgba(255,255,255,0.5)' }}>
        {tabBtn('chat', 'Chat', MessageSquare)}
        {tabBtn('recordings', 'Grabaciones', FileAudio)}
      </div>

      {tab === 'recordings' && <RecordingsTab channelId={channelId} />}

      {tab === 'chat' && (
        <ChannelSearch channelId={channelId} onJump={(id) => {
          const el = document.getElementById(`msg-${id}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.animate?.([{ background: 'rgba(0,122,255,0.12)' }, { background: 'transparent' }], { duration: 1600 });
          }
        }} />
      )}

      {tab === 'chat' && (
        <div className="scrollbar-thin fx-msgs" style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {messages.length === 0 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
              <Radio size={36} className="fx-breathe" style={{ color: '#C7C7CC', marginBottom: 12 }} aria-hidden="true" />
              <p style={{ fontSize: 13, color: '#48484A', fontWeight: 600, margin: 0 }}>Sin mensajes todavía</p>
              <p style={{ fontSize: 12, color: '#6E6E73', margin: '6px 0 0', maxWidth: 280, lineHeight: 1.4 }}>
                Sé el primero en transmitir o escribir un mensaje en este canal.
              </p>
            </div>
          )}

          {messages.map((msg, idx) => {
            const isSelf = msg.userId === currentUser?.id;

            // ── SOS ──
            if (msg.type === 'sos') return (
              <div key={msg.id || idx} className="fx-sos-in" style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', borderRadius: 14,
                background: 'linear-gradient(135deg,#FFE5EC,#FFEAEF)',
                border: '1px solid rgba(255,45,85,0.30)',
                boxShadow: '0 4px 14px rgba(255,45,85,.18)',
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#FF2D55,#FF375F)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 14, flexShrink: 0,
                  boxShadow: '0 4px 10px rgba(255,45,85,.40)',
                }}>🚨</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#FF2D55', letterSpacing: '0.3px' }}>SOS · {msg.username}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#86868B', fontFamily: 'var(--rt-font-mono)' }}>{formatTime(msg.timestamp)}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#1D1D1F', margin: 0, lineHeight: 1.4, fontWeight: 600 }}>{msg.content}</p>
                  {msg.coords && (
                    <a
                      href={`https://www.google.com/maps?q=${msg.coords.lat},${msg.coords.lng}`}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize: 10, color: '#FF2D55', fontWeight: 700, marginTop: 4, display: 'inline-block' }}
                    >
                      Ver ubicación →
                    </a>
                  )}
                </div>
              </div>
            );

            // ── Broadcast ──
            if (msg.type === 'broadcast') return (
              <div key={msg.id || idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px', borderRadius: 14,
                background: '#FFF3DE',
                border: '1px solid rgba(255,159,10,0.20)',
                boxShadow: '0 2px 6px rgba(255,159,10,.10)',
              }}>
                <Megaphone size={14} style={{ color: '#FF9500', marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#FF9500', letterSpacing: '0.3px' }}>📢 {msg.name || 'BROADCAST'}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#86868B', fontFamily: 'var(--rt-font-mono)' }}>{formatTime(msg.timestamp)}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#1D1D1F', margin: 0, lineHeight: 1.4 }}>{msg.message}</p>
                </div>
              </div>
            );

            // ── PTT start ──
            if (msg.type === 'ptt_start') return (
              <div key={msg.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px' }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${msg.avatarColor || '#007AFF'}, #AF52DE)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>
                  {msg.username?.[0]?.toUpperCase()}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#48484A' }}>{msg.username}</span>
                <span style={{ fontSize: 11, color: '#86868B' }}>comenzó a transmitir</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#AEAEB2', fontFamily: 'var(--rt-font-mono)' }}>{formatTime(msg.timestamp)}</span>
              </div>
            );

            // ── PTT end ──
            if (msg.type === 'ptt_end') {
              const lang = (msg.language || 'es').toLowerCase();
              const langLabel = lang.toUpperCase();
              return (
                <div key={msg.id || idx} style={{
                  display: 'flex', flexDirection: 'column', gap: 3,
                  padding: '8px 12px', borderRadius: 12,
                  background: msg.transcription ? '#E6F1FF' : '#F5F5F7',
                  border: '1px solid rgba(0,0,0,0.04)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Mic size={11} style={{ color: '#007AFF', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1D1D1F' }}>{msg.username}</span>
                    {msg.duration && <span style={{ fontSize: 10, color: '#86868B', fontFamily: 'var(--rt-font-mono)' }}>{msg.duration}s</span>}
                    {!msg.transcription && (
                      <span style={{
                        fontSize: 9, padding: '1px 6px', borderRadius: 5,
                        background: '#fff', color: '#007AFF',
                        fontWeight: 700, fontStyle: 'italic',
                        border: '1px solid rgba(0,122,255,.20)',
                        animation: 'pulse 1.2s ease-in-out infinite',
                      }}>ES live</span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#AEAEB2', fontFamily: 'var(--rt-font-mono)' }}>{formatTime(msg.timestamp)}</span>
                  </div>
                  {!msg.transcription && (
                    <div style={{ paddingLeft: 18, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 12, color: '#86868B', fontStyle: 'italic', opacity: 0.7 }}>
                        transcribiendo
                      </span>
                      <span style={{ display: 'inline-flex', gap: 2 }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 3, height: 3, borderRadius: '50%', background: '#007AFF', opacity: 0.7,
                            animation: 'pulse 1s ease-in-out infinite', animationDelay: `${i * 0.15}s`,
                          }} />
                        ))}
                      </span>
                    </div>
                  )}
                  {msg.transcription && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, paddingLeft: 18, animation: 'fadeIn 0.4s ease-out' }}>
                      <span style={{ fontSize: 12, color: '#1D1D1F', lineHeight: 1.4 }}>{msg.transcription}</span>
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 5, background: '#fff', color: '#007AFF', flexShrink: 0, fontWeight: 600, border: '1px solid rgba(0,122,255,.20)' }}>
                        {LANG_FLAGS[lang] || ''} {langLabel}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // ── Text message (iMessage bubble) ──
            if (msg.type === 'text') {
              const isDeleted = !!msg.deletedAt;
              const isEdited = !!msg.editedAt && !isDeleted;
              return (
                <div
                  key={msg.id || idx}
                  id={`msg-${msg.id}`}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: isSelf ? 'flex-end' : 'flex-start', gap: 2 }}
                >
                  {!isSelf && <span style={{ fontSize: 10, color: '#6E6E73', paddingLeft: 6, fontWeight: 600 }}>{msg.username}</span>}
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '8px 13px',
                      borderRadius: isSelf ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      background: isDeleted
                        ? '#F5F5F7'
                        : isSelf ? 'linear-gradient(135deg,#007AFF,#5856D6)' : '#fff',
                      color: isDeleted ? '#86868B' : (isSelf ? '#fff' : '#1D1D1F'),
                      border: isDeleted ? '1px dashed rgba(0,0,0,0.10)' : (isSelf ? 'none' : '1px solid rgba(0,0,0,0.05)'),
                      boxShadow: isDeleted ? 'none' : (isSelf ? '0 4px 12px rgba(0,122,255,.18)' : '0 1px 2px rgba(0,0,0,.04)'),
                      position: 'relative',
                    }}
                  >
                    <MessageContent msg={msg} isSelf={isSelf} />
                    {!isDeleted && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                        <MessageActions msg={msg} channelId={channelId} isSelf={isSelf} currentUser={currentUser} />
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 9, color: '#6E6E73', paddingRight: isSelf ? 6 : 0, paddingLeft: isSelf ? 0 : 6, fontFamily: 'var(--rt-font-mono)' }}>
                    {formatTime(msg.createdAt || msg.timestamp)}
                    {isEdited && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>· editado</span>}
                  </span>
                </div>
              );
            }

            // ── Image message ──
            if (msg.type === 'image') return (
              <div key={msg.id || idx} style={{ display: 'flex', flexDirection: 'column', alignItems: isSelf ? 'flex-end' : 'flex-start', gap: 2 }}>
                {!isSelf && <span style={{ fontSize: 10, color: '#86868B', paddingLeft: 6, fontWeight: 600 }}>{msg.username}</span>}
                <img
                  src={msg.content}
                  alt="imagen"
                  onClick={() => setLightbox(msg.content)}
                  style={{
                    maxWidth: 220, maxHeight: 200,
                    borderRadius: 14,
                    cursor: 'zoom-in',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 6px rgba(0,0,0,.06)',
                    display: 'block',
                  }}
                />
                <span style={{ fontSize: 9, color: '#AEAEB2', paddingRight: isSelf ? 6 : 0, paddingLeft: isSelf ? 0 : 6, fontFamily: 'var(--rt-font-mono)' }}>
                  {formatTime(msg.createdAt || msg.timestamp)}
                </span>
              </div>
            );

            return null;
          })}
          <div ref={bottomRef} />
        </div>
      )}

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
